/**
 * Hints from canvas bakers to the Pixi texture memo (render/pixi/sprite-pool.js), without the
 * bakers importing pixi.js: the jar art is used on the Canvas 2D path and in tests too, and
 * game.js loads Pixi lazily.
 *
 *   - `markTextureNoMipmaps(canvas)`: a picture drawn at its own size (the jar glass, baked at
 *     the renderer density) gains nothing from mipmaps and would pay a third more GPU memory.
 *   - `releaseCanvasTexture(canvas)`: a baked canvas that will not be drawn again (the jar is
 *     rebaked on resize) frees its GPU texture. Textures from canvases are never collected by
 *     Pixi on their own (`autoGarbageCollect` is off), so without this a resize would leak.
 */

var releaser = null;

/** sprite-pool.js registers how a canvas's memoised texture is destroyed. */
export function setCanvasTextureReleaser(fn) {
  releaser = typeof fn === "function" ? fn : null;
}

/** Free the GPU texture made from `canvas` (no-op on Canvas 2D or before Pixi loads). */
export function releaseCanvasTexture(canvas) {
  if (!canvas || !releaser) return;
  try {
    releaser(canvas);
  } catch (_e) { /* a failed release only costs memory */ }
}

/** Ask for a plain linear texture (no mip chain) for this canvas. */
export function markTextureNoMipmaps(canvas) {
  if (canvas) {
    try {
      canvas.textureMipmaps = false;
    } catch (_e) { /* exotic hosts: the default (mipmapped) is still correct */ }
  }
  return canvas;
}

/** @returns {boolean} false when the baker asked for no mipmaps */
export function wantsTextureMipmaps(image) {
  return !(image && image.textureMipmaps === false);
}
